'use client';

import { MemberStatus, Role } from '@prisma/client';
import { useQueryClient } from '@tanstack/react-query';
import {
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ListMembersResponse, MemberPermission, MemberPermissionType } from '@zond/api-interfaces';
import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@zond/ui';
import { ArrowUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useStore } from '../../../store';
import { useDeleteMember, useGetMembers, useUpdateMember } from '../../members/members.queries';
import { DataTablePagination } from './components/data-table-pagination';
import { DataTableToolbar } from './components/data-table-toolbar';
import { columns } from './components/users-columns';
import { UsersDialogs } from './components/users-dialogs';
import { UsersPrimaryButtons } from './components/users-primary-buttons';
import { UsersTable } from './components/users-table';
import { UsersProvider } from './context/users-context';
import { userTypes } from './data/data';
import { EditMemberDialog } from './edit-member-dialog';
import { InviteMembersDialog } from './invite-members-dialog';

export function Members() {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();
  const workspaceId = useStore((state) => state.getSelectedWorkspaceId());
  const { data: members, refetch, isLoading } = useGetMembers();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [editMember, setEditMember] = useState<ListMembersResponse[0] | null>(null);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const updateMember = useUpdateMember(
    () => {
      refetch();
      toast.success(t('members.toasts.memberUpdated'));
    },
    () => {
      toast.error(t('members.toasts.memberUpdateFailed'));
    }
  );

  const deleteMember = useDeleteMember(
    () => {
      toast.success(t('members.toasts.memberDeleted'));
    },
    () => {
      toast.error(t('members.toasts.memberDeleteFailed'));
      refetch();
    }
  );

  const handleSuccess = () => {
    refetch();
  };

  const table = useReactTable({
    data: members || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onColumnFiltersChange: setColumnFilters,
    state: {
      columnFilters,
    },
    filterFns: {
      fuzzy: (row, columnId, value, addMeta) => {
        const searchValue = value.toLowerCase();
        const cellValue = row.getValue(columnId);
        if (typeof cellValue === 'string') {
          return cellValue.toLowerCase().includes(searchValue);
        }
        return false;
      },
      multi: (row, columnId, value, addMeta) => {
        const values = value as unknown[];
        const cellValue = row.getValue(columnId);
        if (Array.isArray(cellValue)) {
          return cellValue.some((val) => values.includes(val));
        }
        return values.includes(cellValue);
      },
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
      columnFilters: [],
    },
  });

  const handleDeleteMember = (memberId: number) => {
    queryClient.setQueryData(['members'], (oldData: typeof members) => {
      return oldData?.filter((member) => member.id !== memberId) ?? [];
    });

    deleteMember.mutate(memberId);
  };

  const handleEditMember = (member: ListMembersResponse[0]) => {
    setEditMember(member);
  };

  return (
    <UsersProvider>
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">{t('members.header.title')}</h1>
            <p className="text-muted-foreground">{t('members.header.description')}</p>
          </div>
          <div className="flex gap-2">
            <InviteMembersDialog
              isOpen={isInviteModalOpen}
              onOpenChange={setIsInviteModalOpen}
              onSuccess={handleSuccess}
            />
          </div>
        </div>

        <div className="space-y-4">
          <DataTableToolbar table={table} />
          <UsersTable
            users={table.getRowModel().rows.map((row) => row.original)}
            onDelete={handleDeleteMember}
            onEdit={handleEditMember}
            onInvite={() => setIsInviteModalOpen(true)}
            isLoading={isLoading}
          />

          <DataTablePagination table={table} />
          <UsersDialogs />
        </div>
      </div>
      {editMember && (
        <EditMemberDialog
          isOpen={!!editMember}
          onOpenChange={(open) => {
            if (!open) setEditMember(null);
          }}
          onSuccess={handleSuccess}
          member={editMember}
        />
      )}
    </UsersProvider>
  );
}

export default Members;
